import { type FC, memo } from "react";

import { LinkedSectionsMenu } from "./LinkedSectionsMenu";
import { SectionIcon } from "./SectionIcon";
import { SessionButton } from "./SessionButton";
import { UnsupportedDeviceBanner } from "./UnsupportedDeviceBanner";
import { ImportSectionButton } from "./content/ImportSectionButton";

import { t } from "i18n/i18n";
import { Chip } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookSectionTemplateIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourseCode,
  selectCurrentCourseTitle,
  selectCurrentOrgName,
  selectCurrentSection,
  selectCurrentSectionHasMaterials,
  selectCurrentTermTitle,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { isLinkedSection, isTemplateSection } from "utils/publishing";
import { Link } from "utils/routing";
import { sectionHomeUrl } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

export const SectionTitleBar: FC = memo(() => {
  const section = useObjectSelector(selectCurrentSection, [
    "id",
    "description",
    "kind",
    "template_id",
    "title",
  ]);
  const isMobile = !useIsMdOrLarger();
  const { canAuthorCourse, canAuthorSection } = useCapabilities();
  const canViewTemplate = canAuthorCourse;
  const orgName = useAppSelector(selectCurrentOrgName);
  const courseCode = useAppSelector(selectCurrentCourseCode);
  const courseTitle = useAppSelector(selectCurrentCourseTitle);
  const termTitle = useAppSelector(selectCurrentTermTitle);
  const hasMaterials = useAppSelector(selectCurrentSectionHasMaterials);
  const showImportButton =
    !isMobile && canAuthorSection && !hasMaterials && !isLinkedSection(section);
  const ccmEnabled = isCCMEnabled();
  const isTemplate = isTemplateSection(section);
  const isLinked = isLinkedSection(section);

  if (!section) {
    return null;
  }

  return (
    <>
      <div className="flex w-full flex-wrap items-center justify-between gap-3 md:pt-4">
        <div className="flex flex-col justify-between gap-3">
          <span className="text-sm font-medium">
            {courseTitle && courseCode
              ? t("common.course_code_and_title", { code: courseCode, title: courseTitle })
              : courseCode || courseTitle}
          </span>

          <div className="flex flex-row items-center gap-3">
            <SectionIcon sectionId={section.id} size={isMobile ? "l" : "xl"} />

            <h2 className="flex items-center justify-between text-xl text-black-tint-20 md:text-2xl">
              {section.title}
            </h2>

            {showImportButton && <ImportSectionButton />}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-black-tint-55">
              {orgName && termTitle && (
                <span>
                  {termTitle}, {orgName}
                </span>
              )}

              {section.description && (
                <TooltipTrigger
                  content={section.description}
                  label={t("tooltip.view.section_description")}
                />
              )}
            </div>

            {ccmEnabled && isTemplate && (
              <>
                <Tooltip content={t("section_view.template_section_tooltip")}>
                  <Chip as="button" kind="hibiscusBorderless">
                    {t("home.add_section_modal.section_type_template_label")}
                  </Chip>
                </Tooltip>

                <LinkedSectionsMenu />
              </>
            )}

            {ccmEnabled && isLinked && (
              <>
                {canAuthorSection && (
                  <Tooltip content={t("section_view.linked_section_tooltip")}>
                    <Chip as="button" kind="mossBorderless">
                      {t("home.add_section_modal.section_type_linked_label")}
                    </Chip>
                  </Tooltip>
                )}

                {canViewTemplate && section.template_id && (
                  <SquareButton
                    as={Link}
                    kind="ghost"
                    size="xs"
                    to={sectionHomeUrl(section.template_id)}
                  >
                    <IconText
                      iconStart={<BookSectionTemplateIcon />}
                      text={t("section_view.view_template")}
                    />
                  </SquareButton>
                )}
              </>
            )}
          </div>
        </div>
        {!isTemplate && <SessionButton />}
      </div>
      <UnsupportedDeviceBanner />
    </>
  );
});

SectionTitleBar.displayName = "SectionTitleBar";
